'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type ControlValues,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { SumSquaredErrorsCalculator } from './sum-squared-errors-calculator';
import { calculateSumSquaredErrors } from '../../../lib/stats/sum-squared-errors';

export interface SumSquaredErrorsControlValues extends ControlValues {
  [ControlIds.Values]: string;
}

const valuesControl: SingleColumnSelectControl = {
  id: ControlIds.Values,
  type: ControlType.SingleColumnSelect,
  label: 'Select Data Column',
  required: true,
  numericOnly: true,
  defaultValue: 'data'
};

export const sumSquaredErrorsControls = {
  tabs: [
    {
      title: 'Data',
      controls: [valuesControl] as const
    }
  ]
};

export const sumSquaredErrorsConfig: CalculatorConfig<CalculatorId.SUM_SQUARED_ERRORS> = {
  id: CalculatorId.SUM_SQUARED_ERRORS,
  type: CalculatorType.Table,
  name: 'Sum of Squared Errors Calculator',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'data',
        name: 'Sample Data', 
        values: ['15', '18', '22', '24', '27', '30', '33', '35', '38', '42'] 
      },
      {
        id: 'errors',
        name: 'Model Errors',
        values: ['2.5', '1.8', '-0.7', '1.2', '-1.5', '0.3', '-0.8', '1.1', '-0.9', '0.5']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: 'data'
  },
  controls: sumSquaredErrorsControls,
  getInitialConfig: (data: TableDataJSON) => ({
    [ControlIds.Values]: data.columns[0]?.id || ''
  }),
  getVisualizationData: (params: { data: TableDataJSON; controls: ControlValues }) => {
    const columnId = params.controls[ControlIds.Values] as string;
    
    if (!columnId) return null;
    
    const column = params.data.columns.find(col => col.id === columnId);
    if (!column) return null;
    
    const values = column.values
      .map(v => {
        const num = Number(v);
        return isNaN(num) ? null : num;
      })
      .filter((v): v is number => v !== null);
    
    if (!values || values.length === 0) {
      return {
        data: [],
        mean: 0,
        sse: 0,
        values: [],
        interpretation: 'No valid numeric data available for calculation.'
      };
    }
    
    return calculateSumSquaredErrors(values);
  },
  render: ({ visualizationData }) => <SumSquaredErrorsCalculator visualizationData={visualizationData} />
}; 